import { useCallback, useState } from 'react';
import { connect } from 'react-redux';
import { FormSpy } from 'react-final-form';
import { FieldArray, FieldArrayRenderProps } from 'react-final-form-arrays';
import { ARRAY_ERROR } from 'final-form';
import { v4 as uuidv4 } from 'uuid';
import spacetime from 'spacetime';

import { ProfKindCodesEnum } from 'APIPackage/DictionariesModule/enums/ProfKindCodesEnum';
import { IDictionaryProfKindSelectOption } from 'CorePackage/DictionariesModule/interfaces/IDictionaryProfKindSelectOption';
import { IKnmFormRef } from 'KnmPackage/MainModule/interfaces/IKnmFormRef';
import {
  getKnmFormRef,
  getKnmIsErknmStatusTypeWaitingCarryOut,
  getKnmIsErknmStatusRemark,
  getKnmIsNeedActSaveOrSign,
} from 'KnmPackage/MainModule/selectors/knmSelectors';
import { getKnoSupervisionStartDate } from 'CorePackage/DictionariesModule/selectors/dictionariesSelectors';
import { DatePickerField } from 'UIPackage/MainModule/components/DatePickerField';
import { IRootState } from 'CorePackage/MainModule/interfaces/IRootState';
import { Row } from 'UIPackage/FormModule/components/Row';

import { IAdminSetting } from '../../../../APIPackage/AdminModule/interfaces/IAdminSetting';
import { isLaterThanAdminSetting } from '../../../../CorePackage/MainModule/validators/isLaterThanAdminSetting';
import { getAdminFetchSettings } from '../../../../PersonalAccountPackage/AdminModule/selectors/adminSelectors';
import { AdminSettingCodesEnum } from '../../../../APIPackage/AdminModule/enums/AdminSettingCodesEnum';
import { formatDate } from '../../../../CorePackage/MainModule/common/formatDate';
import { SYSTEM_DATE_FORMAT_FNS } from '../../../../CorePackage/MainModule/constants/dateFormats';
import { getUserRoleIsSupervisor } from '../../../../AuthPackage/MainModule/selectors/authSelectors';
import { FieldBlock } from '../../../../UIPackage/FormModule/components/FieldBlock';
import { Button } from '../../../../UIPackage/MainModule/components/Button';
import { KnmCollapse } from '../../../../KnmPackage/MainModule/components/KnmCollapse';
import { TextInputField } from '../../../../UIPackage/MainModule/components/TextInputField';
import { composeValidators } from '../../../../CorePackage/MainModule/validators/composeValidators';
import { makeNoLaterThenValidator } from '../../../../CorePackage/MainModule/validators/isNoLaterThen';
import { makeRequired } from '../../../../CorePackage/MainModule/validators/isRequired';
import { IKnmPmFormRescheduling } from '../../../MainModule/interfaces/IKnmPmFormRescheduling';
import styles from './PmRescheduling.module.scss';
import { checkHasErrorInCollapseBlock } from '../../../../functions/checkHasErrorInCollapseBlock';

interface IPmReschedulingProps {
  created: string | undefined;
  isWatchOnly: boolean;
  pmType: IDictionaryProfKindSelectOption | undefined;
  rescheduling: IKnmPmFormRescheduling[] | null | undefined;
}

interface IPmReschedulingStoreProps {
  formRef: IKnmFormRef | undefined;
  isKnmStatusRemark: boolean;
  isKnmStatusTypeWaitingCarryOut: boolean;
  isSupervisor: boolean;
  knoSupervisionStartDate: string | null | undefined;
  settings: IAdminSetting[] | undefined;
  isNeedActSaveOrSign?: boolean;
}

const PmReschedulingWithoutStore = ({
  created,
  isWatchOnly,
  pmType,
  rescheduling,
  formRef,
  isKnmStatusRemark,
  isKnmStatusTypeWaitingCarryOut,
  isSupervisor,
  knoSupervisionStartDate,
  settings,
  isNeedActSaveOrSign = false,
}: IPmReschedulingProps & IPmReschedulingStoreProps) => {
  const [knmCollapseOpened, setKnmCollapseOpened] = useState<{ [key: string]: boolean }>({});
  const remove = formRef?.current.mutators.remove;
  const push = formRef?.current.mutators.push;

  const isTypePP = pmType?.code === ProfKindCodesEnum.PP;

  const canShowReschedulingByStatus = isKnmStatusRemark || isKnmStatusTypeWaitingCarryOut;

  const isUseKnoSupervisorStartDate = isLaterThanAdminSetting(
    created,
    settings,
    AdminSettingCodesEnum.KNM_VALIDATION_4_1_36_STARTS_FROM,
  );

  const minStartDate =
    isUseKnoSupervisorStartDate && knoSupervisionStartDate
      ? new Date(formatDate({ date: knoSupervisionStartDate, dateFormat: SYSTEM_DATE_FORMAT_FNS }))
      : undefined;

  const isDisabledButtonAddNewRescheduling = !!rescheduling && !!rescheduling.find(i => i.isNew);

  const isDisabled =
    isWatchOnly ||
    !isSupervisor ||
    isDisabledButtonAddNewRescheduling ||
    !canShowReschedulingByStatus ||
    isNeedActSaveOrSign;

  /**
   * Функция удаления переноса
   */
  const makeRemoveReschedulingHandler = useCallback(
    (index: number) => () => {
      if (rescheduling) {
        !isWatchOnly && remove && remove(`rescheduling`, index);
      }
    },
    [formRef, remove, isWatchOnly, rescheduling],
  );

  /**
   * Функция добавления переноса
   */
  const addRescheduling = useCallback(() => {
    if (!isWatchOnly && push) {
      const oldStartDate = formRef.current.getFieldState('startDate')?.value;
      const oldEndDate = formRef.current.getFieldState('stopDate')?.value;

      const addingReschedule: IKnmPmFormRescheduling = {
        listId: uuidv4(),
        isNew: true,
        guid: undefined,
        oldStartDate,
        oldEndDate,
      };

      push('rescheduling', addingReschedule);
    }
  }, [formRef, push, isWatchOnly, rescheduling]);

  return (
    <FieldBlock
      label="Сведения о переносе проведения мероприятия"
      margin="top"
      button={
        <Button
          className="shared-btn-add"
          text="Добавить"
          type="button"
          variant="link"
          color="primary"
          onClick={addRescheduling}
          disabled={isDisabled}
        />
      }
    >
      <FormSpy subscription={{ errors: true, touched: true }}>
        {props => {
          const fieldError = props.errors?.rescheduling?.[ARRAY_ERROR];
          const isFieldTouched = props.touched?.rescheduling;

          if (isFieldTouched && fieldError) {
            return <p className="shared-input-error shared-row__small-margin">{fieldError}</p>;
          }

          return null;
        }}
      </FormSpy>
      <FieldArray name={'rescheduling'}>
        {({ fields }: FieldArrayRenderProps<IKnmPmFormRescheduling, HTMLElement>) => {
          if (!fields.length) {
            return null;
          }

          return fields.map((reschedulingFieldName, sIndex) => {
            const fieldValue: IKnmPmFormRescheduling = fields.value?.[sIndex];

            const initiationDate = fieldValue?.initiationDate;
            const isDisableByNeedSaveOrSingAct = isNeedActSaveOrSign;

            const minDateStart = fieldValue?.oldStartDate
              ? new Date(formatDate({ date: fieldValue.oldStartDate, dateFormat: SYSTEM_DATE_FORMAT_FNS }))
              : minStartDate;

            minDateStart?.setDate(minDateStart.getDate() + 1);

            const sNewStartDate = spacetime(
              formatDate({ date: fieldValue?.newStartDate || '', dateFormat: SYSTEM_DATE_FORMAT_FNS }) || '',
            );
            // eslint-disable-next-line no-nested-ternary
            const minDateEnd = fieldValue?.newStartDate ? sNewStartDate.toNativeDate() : minStartDate;

            const oldStartDate = formRef?.current.getFieldState('startDate')?.value;
            const maxDateStart = spacetime(oldStartDate || '')
              .endOf('year')
              .toNativeDate();

            const hasError = checkHasErrorInCollapseBlock(formRef, reschedulingFieldName);

            return (
              <KnmCollapse
                id={`rescheduling[${fieldValue.listId}]`}
                theme="fields"
                title={`Перенос проведения мероприятия ${initiationDate ? `от ${initiationDate}` : ''} `}
                isNew={fieldValue.isNew}
                opened={fieldValue.isNew}
                onClose={
                  !isWatchOnly && !isDisableByNeedSaveOrSingAct && fieldValue.isNew
                    ? makeRemoveReschedulingHandler(sIndex)
                    : undefined
                }
                key={`rescheduling[${fieldValue.listId}]`}
                changeOpenedCallback={e =>
                  setKnmCollapseOpened(state => ({
                    ...state,
                    [reschedulingFieldName]: e,
                  }))
                }
                error={hasError && knmCollapseOpened[reschedulingFieldName]}
              >
                <>
                  <Row>
                    <FieldBlock label={'Новая дата начала ПМ'} size="half" placementLabel="left" color="black">
                      <DatePickerField
                        placeholder="Выберите дату"
                        className={styles.RescheduilngNewDates}
                        id={`rescheduling[${fieldValue.listId}].newStartDate`}
                        name={`${reschedulingFieldName}.newStartDate`}
                        disabled={isWatchOnly || !fieldValue.isNew || isDisableByNeedSaveOrSingAct}
                        minDate={minDateStart}
                        maxDate={maxDateStart}
                        validate={composeValidators(
                          makeNoLaterThenValidator(
                            'Дата начала должна быть меньше или равна дате окончания',
                            () => fieldValue?.newEndDate,
                          ),
                          makeRequired('Поле не может быть пустым'),
                        )}
                      />
                    </FieldBlock>
                    {isTypePP && (
                      <FieldBlock label={'Новая дата окончания ПМ'} size="half" placementLabel="left" color="black">
                        <DatePickerField
                          placeholder="Выберите дату"
                          className={styles.RescheduilngNewDates}
                          id={`rescheduling[${fieldValue.listId}].newEndDate`}
                          name={`${reschedulingFieldName}.newEndDate`}
                          disabled={
                            isWatchOnly ||
                            !fieldValue.isNew ||
                            !fieldValue?.newStartDate ||
                            isDisableByNeedSaveOrSingAct
                          }
                          minDate={minDateEnd}
                        />
                      </FieldBlock>
                    )}
                  </Row>
                  <Row>
                    <FieldBlock label={'Дата начала до переноса ПМ'} size="half" placementLabel="left" color="black">
                      <TextInputField
                        id={`rescheduling[${fieldValue.listId}].oldStartDate`}
                        name={`${reschedulingFieldName}.oldStartDate`}
                        disabled={true}
                      />
                    </FieldBlock>
                    {isTypePP && (
                      <FieldBlock
                        label={'Дата окончания до переноса ПМ'}
                        size="half"
                        placementLabel="left"
                        color="black"
                      >
                        <TextInputField
                          id={`rescheduling[${fieldValue.listId}].oldEndDate`}
                          name={`${reschedulingFieldName}.oldEndDate`}
                          disabled={true}
                        />
                      </FieldBlock>
                    )}
                  </Row>
                  <Row>
                    <FieldBlock label={'Комментарий'} labelSize="col-4" color="black">
                      <TextInputField
                        id={`rescheduling[${fieldValue.listId}].comment`}
                        name={`${reschedulingFieldName}.comment`}
                        disabled={isWatchOnly || !fieldValue.isNew || isDisableByNeedSaveOrSingAct}
                      />
                    </FieldBlock>
                  </Row>
                </>
              </KnmCollapse>
            );
          });
        }}
      </FieldArray>
    </FieldBlock>
  );
};

const mapStateToProps = (state: IRootState): IPmReschedulingStoreProps => ({
  formRef: getKnmFormRef(state),
  isKnmStatusRemark: getKnmIsErknmStatusRemark(state),
  isKnmStatusTypeWaitingCarryOut: getKnmIsErknmStatusTypeWaitingCarryOut(state),
  isSupervisor: getUserRoleIsSupervisor(state),
  knoSupervisionStartDate: getKnoSupervisionStartDate(state),
  settings: getAdminFetchSettings(state),
  isNeedActSaveOrSign: getKnmIsNeedActSaveOrSign(state),
});

export const PmRescheduling = connect(mapStateToProps)(PmReschedulingWithoutStore);
